import clsx from 'clsx';
import React, {
  Suspense, useCallback, useMemo, useState
} from 'react';
import { CgFileDocument } from 'react-icons/cg';
import { FaRegEdit } from 'react-icons/fa';
import { FaCircleCheck } from 'react-icons/fa6';
import { FcProcess } from 'react-icons/fc';
import { FiArrowLeft, FiMoreVertical } from 'react-icons/fi';
import { HiTrash } from 'react-icons/hi';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { PiClockCountdownBold } from 'react-icons/pi';
import { RiContractLine } from 'react-icons/ri';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { Heading } from '@/components/typography/heading';
import {
  useContract, useContractStatus, useDeleteContract, useTaskStatus
} from '@/hooks/queries/useDataContracts';
import { useAuthContext } from '@/hooks/useAuthContext';
import { ContractStatus } from '@/types/dataContracts';
import { parseParams } from '@/utils/searchParams';
import { parseDataContractYAML } from '@/utils/yamlParser';
import { getContractSecretName } from '@/utils/yamlSecretEdit';

import { ActionMenu, ActionMenuItem } from '../ActionMenu';
import { CredentialsSection } from './components/CredentialsSection/CredentialsSection';
import { DataModelSection } from './components/DataModelSection';
import { EntityNode } from './components/EntityNode';
import { PropertiesSection } from './components/PropertiesSection';
import { ServersSection } from './components/ServersSection';
import styles from './contractDetails.module.scss';
import EditCredentialModal from './EditCredentialModal';
import { useDiagramBuilder } from './hooks/useDiagramBuilder';
import { useTroubleshootingTips } from './hooks/useTroubleshootingTips';
import TaskHistoryModal from './TaskHistoryModal';
import UpdateContractModal from './UpdateContractModal';
import YAMLViewerModal from './YAMLViewerModal';

const statusConfig: Record<ContractStatus, { icon: React.ReactNode; label: string; description: string }> = {
  [ContractStatus.DRAFT]: {
    icon: <PiClockCountdownBold className={ styles.statusIconDraft } />,
    label: 'Draft',
    description: 'Contract is in draft state.'
  },
  [ContractStatus.QUEUED]: {
    icon: <PiClockCountdownBold className={ styles.statusIconQueued } />,
    label: 'Queued',
    description: 'Contract is waiting to be processed.'
  },
  [ContractStatus.PROCESSING]: {
    icon: <FcProcess className={ styles.statusIconProcessing } />,
    label: 'Processing',
    description: 'Contract processing is in progress.'
  },
  [ContractStatus.COMPLETED]: {
    icon: <FaCircleCheck className={ styles.statusIconCompleted } />,
    label: 'Completed',
    description: 'Contract has been processed successfully.'
  },
  [ContractStatus.FAILED]: {
    icon: <IoCloseCircleSharp className={ styles.statusIconFailed } />,
    label: 'Failed',
    description: 'Contract processing failed.'
  },
  [ContractStatus.DELETING]: {
    icon: <HiTrash className={ styles.statusIconDeleting } />,
    label: 'Deleting',
    description: 'Contract is being deleted.'
  }
};


function DataContractDetailsPage() {
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const { id: contractId } = parseParams<{ id?: string }>( searchParams );
  const [ showProcessingModal, setShowProcessingModal ] = useState( false );
  const [ showYAMLModal, setShowYAMLModal ] = useState( false );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ showUpdateModal, setShowUpdateModal ] = useState( false );
  const [ showEditCredentialModal, setShowEditCredentialModal ] = useState( false );

  const { sessionContext, canAccess } = useAuthContext();
  const { data: contract, isLoading, error } = useContract( contractId ?? null );
  const { data: contractStatus } = useContractStatus( contractId ?? null );
  const deleteContractMutation = useDeleteContract();

  const latestTaskId = contractStatus?.latest_task?.task_id ?? null;
  const shouldFetchTask = ( showProcessingModal || contract?.status === ContractStatus.FAILED ) && !!latestTaskId;
  const { data: taskStatus, isLoading: isTaskLoading } = useTaskStatus(
    latestTaskId,
    shouldFetchTask
  );

  const parsedYAML = useMemo( () => {
    return parseDataContractYAML( contract?.yaml_content );
  }, [ contract?.yaml_content ] );

  const formatContact = ( contact: any ) => {
    if ( !contact ) return '-';

    const contactParts: string[] = [];

    if ( contact.name ) contactParts.push( contact.name );
    if ( contact.email ) contactParts.push( contact.email );
    if ( contact.url ) contactParts.push( contact.url );
    if ( contact.phone ) contactParts.push( contact.phone );

    // Add any other fields that aren't the standard ones
    Object.keys( contact ).forEach( key => {
      if ( ![ 'name', 'email', 'url', 'phone' ].includes( key ) ) {
        contactParts.push( `${ key }: ${ contact[key] }` );
      }
    } );

    return contactParts.length > 0 ? contactParts.join( ' | ' ) : '-';
  };

  // Use diagram builder hook
  const { nodes: diagramNodes, edges: diagramEdges } = useDiagramBuilder( parsedYAML );

  // Define node types for React Flow
  const nodeTypes = useMemo( () => ( { entity: EntityNode } ), [] );

  const handleOpenDeleteModal = useCallback( () => {
    setIsDeleteModalOpen( true );
  }, [] );

  const handleCloseDeleteModal = useCallback( () => {
    setIsDeleteModalOpen( false );
  }, [] );

  const handleConfirmDelete = useCallback( async() => {
    if ( !contract ) return;
    await deleteContractMutation.mutateAsync( contract.contract_id );
    handleCloseDeleteModal();
    toast.success( 'Contract deletion started' );
    setTimeout( () => navigate( '/dataCatalog' ), 1500 );
  }, [ contract, deleteContractMutation, handleCloseDeleteModal, navigate ] );

  const handleUpdateContract = useCallback( () => {
    setShowUpdateModal( true );
  }, [] );

  const troubleshootingTips = useTroubleshootingTips( {
    errorMessage: contractStatus?.latest_task?.error || taskStatus?.error,
    taskType: contractStatus?.latest_task?.task_type,
    parsedYAML,
    onShowYAML: () => setShowYAMLModal( true ),
    onUpdateContract: handleUpdateContract,
    onShowProcessingModal: () => setShowProcessingModal( true )
  } );

  if ( !contractId ) {
    return (
      <div className='page' data-testid='contract-details-page'>
        <div className={ styles.backButtonContainer }>
          <Button
            variant='secondary'
            icon={ <Icon icon={ FiArrowLeft } /> }
            asLink
            href='/dataCatalog'
            data-testid='back-button'
          >
            Back to Data Contracts
          </Button>
        </div>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <p>No contract selected. Please select a contract from the list.</p>
        </div>
      </div>
    );
  }

  const config = contract ? ( statusConfig[contract.status] ?? statusConfig[ContractStatus.DRAFT] ) : null;

  return (
    <div className='page' data-testid='contract-details-page'>
      <ToastContainer />

      <DeleteModal
        isOpen={ isDeleteModalOpen }
        onClose={ handleCloseDeleteModal }
        onDelete={ handleConfirmDelete }
        entityName={ contract?.name || '' }
        entityType="contract"
      />

      { showProcessingModal && latestTaskId && (
        <TaskHistoryModal
          taskStatus={ taskStatus }
          isLoading={ isTaskLoading }
          onClose={ () => setShowProcessingModal( false ) }
        />
      ) }

      { showYAMLModal && contract && (
        <YAMLViewerModal
          yamlContent={ contract.yaml_content || '' }
          contractName={ contract.contract_id }
          onClose={ () => setShowYAMLModal( false ) }
        />
      ) }

      { showUpdateModal && contract && (
        <UpdateContractModal
          contractId={ contract.contract_id }
          contractName={ contract.name }
          currentYaml={ contract.yaml_content || '' }
          onClose={ () => setShowUpdateModal( false ) }
        />
      ) }

      { showEditCredentialModal && contract && (
        <EditCredentialModal
          contractId={ contract.contract_id }
          contractName={ contract.name }
          currentYaml={ contract.yaml_content || '' }
          currentSecretName={ contract.secret_name ?? getContractSecretName( contract.yaml_content ) }
          onClose={ () => setShowEditCredentialModal( false ) }
        />
      ) }

      <div className={ styles.backButtonContainer }>
        <Button
          variant='secondary'
          icon={ <Icon icon={ FiArrowLeft } /> }
          asLink
          href='/dataCatalog'
          data-testid='back-button'
        >
          Back to Data Contracts
        </Button>
      </div>

      { isLoading ? (
        <Loader />
      ) : error ? (
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <p data-testid='error-message'>Failed to load contract details. Please try again.</p>
        </div>
      ) : contract ? (
        <>
          <div className={ clsx( 'layout__row', 'pad', 'layout__info', styles.noBorder ) }>
            <div className={ styles.headerContent }>
              <div className={ styles.titleIconBox }>
                <Icon icon={ RiContractLine } className={ styles.titleIcon } />
              </div>
              <div className={ styles.titleTextContainer }>
                <Heading as='h2' data-testid='contract-name'>
                  { parsedYAML?.info?.title || contract.name }
                </Heading>
                { parsedYAML?.info?.description && (
                  <p className={ styles.description } data-testid='contract-description'>
                    <span className={ styles.descriptionLabel }>Description:</span> { parsedYAML.info.description }
                  </p>
                ) }
              </div>
            </div>
            <div className={ styles.headerActions }>
              <Button
                variant='primary'
                size='sm'
                onClick={ () => setShowYAMLModal( true ) }
                icon={ <Icon icon={ CgFileDocument } /> }
                data-testid='show-yaml-button'
              >
                Show YAML
              </Button>
              <ActionMenu icon={ <Icon icon={ FiMoreVertical } size="md" /> } disabled={ !( canAccess( 'can_manage_users' ) || contract.submitted_by === sessionContext?.userId ) } data-testid='action-menu'>
                <ActionMenuItem
                  value="Update contract"
                  icon={ <FaRegEdit size={ 16 } /> }
                  onClick={ handleUpdateContract }
                  data-testid='menu-update-contract'
                />
                <ActionMenuItem
                  value="Delete contract"
                  icon={ <HiTrash size={ 16 } /> }
                  onClick={ handleOpenDeleteModal }
                  data-testid='menu-delete-contract'
                />
              </ActionMenu>
            </div>
          </div>


          { /* Properties Section */ }
          <PropertiesSection
            contract={ contract }
            config={ config }
            contractStatus={ contractStatus }
            taskStatus={ taskStatus }
            parsedYAML={ parsedYAML }
            latestTaskId={ latestTaskId }
            troubleshootingTips={ troubleshootingTips }
            formatContact={ formatContact }
            onShowProcessingModal={ () => setShowProcessingModal( true ) }
          />
          { /* Credentials Section */ }
          <CredentialsSection
            contract={ contract }
            parsedYAML={ parsedYAML }
            canEdit={ canAccess( 'can_manage_users' ) || contract.submitted_by === sessionContext?.userId }
            onEdit={ () => setShowEditCredentialModal( true ) }
          />
          { /* Servers Section */ }
          <ServersSection servers={ parsedYAML?.servers } />


          { /* Data Model Section */ }
          { parsedYAML?.models && Object.keys( parsedYAML.models ).length > 0 && (
            <DataModelSection
              models={ parsedYAML.models }
              diagramNodes={ diagramNodes }
              diagramEdges={ diagramEdges }
              nodeTypes={ nodeTypes }
            />
          ) }
        </>
      ) : null }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <DataContractDetailsPage />
    </Suspense>
  );
}
